import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Container, Button, IconButton, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    CircularProgress, Menu, MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuthContext } from '../context/AuthContext';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType } from '../schema';
import { getProviderCases, ProviderCase } from '../queries/getprovidercases';

const MyCases: React.FC = () => {
    const navigate = usePersistentNavigate();
    const [searchParams] = useSearchParams();
    const { accessToken, logout, userType, setAccessToken } = useAuthContext();
    const [cases, setCases] = useState<ProviderCase[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(-1);

    // Menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
    const handleMenuClose = () => setMenuAnchorEl(null);
    const handleMenuHome = () => {
        handleMenuClose();
        navigate('/');
    };
    const handleMenuLogout = async () => {
        handleMenuClose();
        await logout();
    };

    const fetchCases = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            const response = await getProviderCases(
                { offset: page * rowsPerPage, pageSize: rowsPerPage },
                accessToken,
                setAccessToken,
                logout
            );
            setCases(response.cases);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error("Failed to fetch cases:", error);
        } finally {
            setLoading(false);
        }
    }, [accessToken, page, rowsPerPage, setAccessToken, logout]);

    useEffect(() => {
        // Redirect if not logged in or not a provider
        if (!accessToken) {
            navigate('/login');
            return;
        }
        if (userType === UserType.ADMIN) {
            // Admins should use the admin portal
            navigate('/admin');
            return;
        }
    }, []);

    useEffect(() => {
        if (accessToken) {
            fetchCases();
        }
    }, [accessToken, fetchCases]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Helper functions to format values
    const formatStatus = (status: number) => {
        const statusMap: { [key: number]: string } = {
            1: 'Processing Input',
            2: 'Creating Report',
            3: 'Report Generated',
            4: 'Generation Failed',
            5: 'Pending Assignment',
            6: 'Pending Review',
            7: 'Review Finalized'
        };
        return statusMap[status] || 'Unknown';
    };

    const formatCancerType = (cancerType: number) => {
        const cancerTypeMap: { [key: number]: string } = {
            1: 'Breast',
            2: 'Lung',
            3: 'Colon',
            4: 'Prostate'
        };
        return cancerTypeMap[cancerType] || 'Unspecified';
    };

    const formatVerbosity = (verbosity?: number) => {
        if (!verbosity) return '-';
        const verbosityMap: { [key: number]: string } = {
            1: 'Low',
            2: 'Medium',
            3: 'High'
        };
        return verbosityMap[verbosity] || 'Unspecified';
    };

    const formatReasoning = (reasoning?: number) => {
        if (!reasoning) return '-';
        const reasoningMap: { [key: number]: string } = {
            1: 'Low',
            2: 'Medium',
            3: 'High'
        };
        return reasoningMap[reasoning] || 'Unspecified';
    };

    const calculateDuration = (startTime?: Date, endTime?: Date) => {
        if (!startTime || !endTime) return '-';
        const durationMs = endTime.getTime() - startTime.getTime();
        const durationSeconds = Math.round(durationMs / 1000);
        if (durationSeconds < 60) return `${durationSeconds}s`;
        const minutes = Math.floor(durationSeconds / 60);
        const seconds = durationSeconds % 60;
        return `${minutes}m ${seconds}s`;
    };

    const formatSubmitTime = (submitTime?: Date) => {
        if (!submitTime) return '-';
        return submitTime.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (cases.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        You haven't submitted any cases yet.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/')}
                    >
                        Submit Your First Case
                    </Button>
                </Box>
            );
        }

        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Case ID</TableCell>
                            <TableCell>Cancer Type</TableCell>
                            <TableCell>Submit Time (ET)</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>AI Job Duration</TableCell>
                            <TableCell>Verbosity</TableCell>
                            <TableCell>Complexity</TableCell>
                            <TableCell>Case Report</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cases.map((caseItem) => {
                            const newSearch = new URLSearchParams();
                            newSearch.set('case_id', caseItem.id);
                            if (searchParams.has('stagingEnv')) {
                                newSearch.set('stagingEnv', searchParams.get('stagingEnv')!);
                            }
                            const caseReportUrl = `/case-report?${newSearch.toString()}`;

                            return (
                                <TableRow key={caseItem.id}>
                                    <TableCell>{caseItem.id.substring(0, 8)}...</TableCell>
                                    <TableCell>{formatCancerType(caseItem.cancerType)}</TableCell>
                                    <TableCell>{formatSubmitTime(caseItem.submitTime)}</TableCell>
                                    <TableCell>{formatStatus(caseItem.status)}</TableCell>
                                    <TableCell>{calculateDuration(caseItem.aiJobStartTime, caseItem.aiJobEndTime)}</TableCell>
                                    <TableCell>{formatVerbosity(caseItem.verbosity)}</TableCell>
                                    <TableCell>{formatReasoning(caseItem.reasoning)}</TableCell>
                                    <TableCell>
                                        <Button
                                            component="a"
                                            href={caseReportUrl}
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate({
                                                    pathname: '/case-report',
                                                    search: newSearch.toString()
                                                });
                                            }}
                                        >
                                            View Report
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#ffffff' }}>
            <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'white', borderBottom: '1px solid #e6e6e6' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box
                        component="a"
                        href="/"
                        sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                        <Box
                            component="img"
                            src="/logo.jpg"
                            alt="Precisca"
                            sx={{
                                height: { xs: 28, sm: 36 },
                                width: 'auto',
                                display: 'block',
                                mr: 1
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            sx={{ color: '#2c5282' }}
                            aria-controls={menuOpen ? 'top-right-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen ? 'true' : undefined}
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="top-right-menu"
                            anchorEl={menuAnchorEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={handleMenuHome}>Home</MenuItem>
                            <MenuItem onClick={() => { handleMenuClose(); navigate('/my-cases'); }}>My Cases</MenuItem>
                            <MenuItem onClick={handleMenuLogout}>Log Out</MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ mx: 2 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e6e6e6' }}>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: '#2c5282' }}>
                            My Cases
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            View and manage your submitted cases
                        </Typography>
                    </Box>
                    {renderTable()}
                    {cases.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default MyCases;
